import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';
import type { ReactNode } from 'react';
import { authApi } from '../api/endpoints';
import type { AuthUser, PermissionCode } from '../types';

interface AuthState {
  user: AuthUser | null;
  roles: string[];
  permissions: Set<string>;
}

interface AuthContextValue extends AuthState {
  /** true khi đang gọi /auth/me lần đầu (ProtectedRoute phải chờ). */
  loading: boolean;
  /** Đã xác thực hay chưa. */
  isAuthenticated: boolean;
  /** Đăng nhập (email hoặc username) rồi nạp lại thông tin phiên. */
  login: (identifier: string, password: string) => Promise<void>;
  /** Đăng nhập bằng ID token của Google rồi nạp lại phiên. */
  loginWithGoogle: (credential: string) => Promise<void>;
  /** Đăng xuất + xóa state. */
  logout: () => Promise<void>;
  /** Nạp lại /auth/me (sau khi quyền thay đổi). */
  refresh: () => Promise<void>;
  /** Kiểm tra một permission code. */
  hasPermission: (code: PermissionCode) => boolean;
}

const EMPTY_STATE: AuthState = {
  user: null,
  roles: [],
  permissions: new Set<string>(),
};

const AuthContext = createContext<AuthContextValue | undefined>(undefined);

export function AuthProvider({ children }: { children: ReactNode }): JSX.Element {
  const [state, setState] = useState<AuthState>(EMPTY_STATE);
  const [loading, setLoading] = useState(true);

  const loadMe = useCallback(async () => {
    try {
      const me = await authApi.me();
      setState({
        user: me.user,
        roles: me.roles,
        permissions: new Set(me.permissions),
      });
    } catch {
      // 401 = chưa đăng nhập → giữ trạng thái rỗng.
      setState(EMPTY_STATE);
    }
  }, []);

  useEffect(() => {
    let active = true;
    void (async () => {
      await loadMe();
      if (active) setLoading(false);
    })();
    return () => {
      active = false;
    };
  }, [loadMe]);

  const login = useCallback(
    async (identifier: string, password: string) => {
      await authApi.login(identifier, password);
      await loadMe();
    },
    [loadMe],
  );

  const loginWithGoogle = useCallback(
    async (credential: string) => {
      await authApi.googleLogin(credential);
      await loadMe();
    },
    [loadMe],
  );

  const logout = useCallback(async () => {
    try {
      await authApi.logout();
    } finally {
      setState(EMPTY_STATE);
    }
  }, []);

  const hasPermission = useCallback(
    (code: PermissionCode) => state.permissions.has(code),
    [state.permissions],
  );

  const value = useMemo<AuthContextValue>(
    () => ({
      ...state,
      loading,
      isAuthenticated: state.user !== null,
      login,
      loginWithGoogle,
      logout,
      refresh: loadMe,
      hasPermission,
    }),
    [state, loading, login, loginWithGoogle, logout, loadMe, hasPermission],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

/** Hook truy cập AuthContext (ném lỗi nếu dùng ngoài provider). */
export function useAuth(): AuthContextValue {
  const ctx = useContext(AuthContext);
  if (!ctx) {
    throw new Error('useAuth phải được dùng bên trong <AuthProvider>.');
  }
  return ctx;
}
